import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';
import ToggleButton from '../UI/ToggleButton';
import ModalWrapper from '../UI/ModalWrapper';
import CreateTecnicoModal from '../Modal/CreaTecniciModal';
import usePost from '../../Hooks/usePost';
import API_ENDPOINTS from '../../Config/api';
import ClipLoader from 'react-spinners/ClipLoader';
import SearchBar from '../UI/Searchbar';
import { Icon } from '../UI/Icon';

export default function ListTecniciTable({
  data,
  setData,       // <-- viene dal parent
  refetchData
}) {
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [childrenModal, setChildrenModal] = useState(null);

  // un solo hook per create/toggle
  const { postData, loading } = usePost();

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setChildrenModal(null);
  };

  const handleOpenModal = (component) => {
    setChildrenModal(component);
    setIsModalOpen(true);
  };

  // CREA TECNICO: prima POST, poi aggiorno stato parent se ok
const handleCreateTecnico = async (formData) => {
  try {
    const response = await postData(API_ENDPOINTS.creaTecnicoMSSP, formData);

    if (response && (response.id_tecnico || response.id)) {
      // ⬇️ append come ultimo elemento
      setData(prev => Array.isArray(prev) ? [...prev, response] : [response]);
    } else {
      // fallback: se l'API non ritorna l'oggetto completo, fai refetch
      refetchData?.();
    }

    const msg = `Tecnico creato. Email di attivazione inviata a ${response?.email || formData.email}.`;
    toast.success(msg);
    handleCloseModal();
  } catch {
    toast.error("Errore durante la creazione del tecnico");
  }
};

  // TOGGLE: prima POST, poi aggiorno stato parent se ok (no ottimistico)
  const handleToggleTecnico = async (tecnico) => {
    const id = tecnico.id_tecnico;
    const isAttivo = String(tecnico.stato ?? '').toLowerCase() === 'attivo';

    try {
      if (isAttivo) {
        await postData(API_ENDPOINTS.disabilitaTecnicoMSSP(id), {});
        toast.success('Tecnico disabilitato con successo');
      } else {
        await postData(API_ENDPOINTS.abilitaTecnicoMSSP(id), {});
        toast.success('Tecnico abilitato con successo');
      }

      // aggiorno lo stato nel parent SOLO dopo il successo
      setData(prev =>
        (Array.isArray(prev) ? prev : []).map(t =>
          t.id_tecnico === id
            ? { ...t, stato: isAttivo ? 'disattivo' : 'attivo' }
            : t
        )
      );

      // opzionale: per allinearsi perfettamente al backend
      // refetchData?.();
    } catch (e) {
      toast.error("Errore durante l'aggiornamento dello stato del tecnico");
    }
  };

  const filteredData = (Array.isArray(data) ? data : []).filter((item) => {
    const s = search.toLowerCase();
    return (item.nome ?? '').toLowerCase().includes(s);
  });

  const loader = (
    <div className='w-full h-full flex justify-center items-center'>
      <ClipLoader size={60} />
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex justify-between">
        <SearchBar
          search={search}
          setSearch={setSearch}
          placeholder={null}
        />
        <button
          className="flex items-center gap-2 bg-white text-gray-800 shadow-md hover:shadow-lg transition-all duration-300 px-3 py-2 rounded-md active:scale-95"
          onClick={() =>
            handleOpenModal(
              <CreateTecnicoModal
                onClose={handleCloseModal}
                onSubmit={handleCreateTecnico}
              />
            )
          }
        >
          <Icon name={'plus'} size={18} />
          Crea Tecnico
        </button>
      </div>

      <div className="flex flex-col h-[32rem] ">
        <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable] border-[#D1D5DB] border rounded">
          <table className="w-full table-auto text-sm text-slate-200 border-collapse">
            <colgroup>
              <col className="w-[50%]" />
              <col className="w-[20%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
            </colgroup>

            <thead className=" text-black text-sm sticky top-0 z-10 bg-[#F3F4F7]">
              <tr>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Nome</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Email</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Telefono</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Ruolo</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Stato</th>
              </tr>
            </thead>

            <tbody>
              {filteredData && filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <Row
                    key={item.id_tecnico}
                    item={item}
                    onToggle={handleToggleTecnico}
                    toggling={loading}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-6 text-slate-400">
                    Nessun tecnico trovato
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModalWrapper position="center" isOpen={isModalOpen} onClose={handleCloseModal}>
        {childrenModal}
      </ModalWrapper>
    </div>
  );
}

/* ---------------- Row: stateless, controllato dal parent ---------------- */

function Row({
  item,
  onToggle,
  toggling
}) {
  return (
    <tr className="text-black border-white border-b-4 bg-[#E3E3E3]">
      {/* Nome */}
      <td className="px-3 py-3 ">
        <p className="font-semibold leading-tight whitespace-nowrap">
          {`${item.nome} ${item.cognome}` ?? "--"}
        </p>
      </td>

      {/* Email */}
      <td className="px-3 py-3 text-left tabular-nums">
        {`${item.email}` ?? "--"}
      </td>

      {/* Telefono */}
      <td className="px-3 py-3 text-left tabular-nums">
        {`${item.telefono}` ?? "--"}
      </td>

      {/* Ruolo */}
      <td className="px-3 py-3 text-left tabular-nums">
        {`${item.ruolo}` ?? "--"}
      </td>

      {/* Stato */}
      <td className="px-3 py-3 text-left tabular-nums">
        <ToggleButton
          checked={String(item.stato ?? '').toLowerCase() === 'attivo'}
          onToggle={() => onToggle(item)}
          disabled={toggling}
        />
      </td>
    </tr>
  );
}
